<template>
  <v-dialog v-model="show" max-width="800px" @keydown.esc="show = false">
    <v-card>
      <v-card-title>
        <v-icon class="mr-2">mdi-needle</v-icon>
        Stickerei Produkte
        <v-spacer></v-spacer>
        <v-btn icon @click="show = false"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>

      <v-card-text class="pb-0">
        <v-row align="center" class="mb-2">
          <v-col>
            <v-text-field
              v-model="search"
              prepend-inner-icon="mdi-magnify"
              label="Suchen..."
              hide-details
              dense
              outlined
              clearable
            ></v-text-field>
          </v-col>
          <v-col cols="auto">
            <v-btn color="primary" small @click="openForm()">
              <v-icon left small>mdi-plus</v-icon>
              Produkt hinzufügen
            </v-btn>
          </v-col>
        </v-row>

        <v-data-table
          :headers="headers"
          :items="products"
          :search="search"
          :loading="loading"
          dense
          class="elevation-1"
          no-data-text="Keine Produkte vorhanden"
        >
          <template v-slot:item.actions="{ item }">
            <v-btn icon x-small @click="openForm(item)">
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
            <v-btn icon x-small color="error" @click="confirmDelete(item)">
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="show = false">Schließen</v-btn>
      </v-card-actions>
    </v-card>

    <!-- Create / Edit Form Dialog -->
    <v-dialog v-model="form.show" max-width="480px" @keydown.esc="form.show = false">
      <v-card>
        <v-card-title>{{ form.id ? 'Produkt bearbeiten' : 'Neues Produkt' }}</v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="form.valid">
            <v-text-field
              v-model="form.name"
              label="Name *"
              outlined
              dense
              :rules="[v => !!v || 'Pflichtfeld']"
              class="mb-2"
            ></v-text-field>
            <v-text-field
              v-model="form.sku"
              label="SKU *"
              outlined
              dense
              :rules="[v => !!v || 'Pflichtfeld']"
              class="mb-2"
            ></v-text-field>
            <v-text-field
              v-model="form.ean"
              label="EAN"
              outlined
              dense
              class="mb-2"
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="form.show = false" :disabled="form.saving">Abbrechen</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="saveProduct" :loading="form.saving" :disabled="!form.valid">
            Speichern
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirm Dialog -->
    <v-dialog v-model="deleteDialog.show" max-width="360px">
      <v-card>
        <v-card-title>Produkt löschen?</v-card-title>
        <v-card-text>
          <strong>{{ deleteDialog.item && deleteDialog.item.name }}</strong> ({{ deleteDialog.item && deleteDialog.item.sku }}) wirklich löschen?
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="deleteDialog.show = false">Abbrechen</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="error" @click="deleteProduct" :loading="deleteDialog.deleting">Löschen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" top :timeout="3000">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">OK</v-btn>
      </template>
    </v-snackbar>
  </v-dialog>
</template>

<script>
import productionService from "@/services/ProductionService";

export default {
  name: "EmbroideryProductsDialog",
  data: () => ({
    show: false,
    loading: false,
    search: "",
    products: [],
    headers: [
      { text: "Name", value: "name" },
      { text: "SKU", value: "sku" },
      { text: "EAN", value: "ean" },
      { text: "", value: "actions", sortable: false, align: "end" },
    ],
    form: {
      show: false,
      valid: false,
      saving: false,
      id: null,
      name: "",
      sku: "",
      ean: "",
    },
    deleteDialog: {
      show: false,
      deleting: false,
      item: null,
    },
    snackbar: {
      show: false,
      text: "",
      color: "success",
    },
  }),
  methods: {
    open() {
      this.show = true;
      this.fetchProducts();
    },

    async fetchProducts() {
      this.loading = true;
      try {
        const res = await productionService.getEmbroideryProducts();
        this.products = res.data;
      } catch (err) {
        this.notify("Fehler beim Laden der Produkte", "error");
      } finally {
        this.loading = false;
      }
    },

    openForm(item = null) {
      this.form.id = item ? item.id : null;
      this.form.name = item ? item.name : "";
      this.form.sku = item ? item.sku : "";
      this.form.ean = item ? (item.ean || "") : "";
      this.form.saving = false;
      this.form.show = true;
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
    },

    async saveProduct() {
      if (!this.$refs.form.validate()) return;
      this.form.saving = true;
      const payload = { name: this.form.name, sku: this.form.sku, ean: this.form.ean || null };
      try {
        if (this.form.id) {
          await productionService.updateEmbroideryProduct(this.form.id, payload);
          this.notify("Produkt aktualisiert");
        } else {
          await productionService.createEmbroideryProduct(payload);
          this.notify("Produkt erstellt");
        }
        this.form.show = false;
        await this.fetchProducts();
      } catch (err) {
        const msg = err.response?.data?.error || "Fehler beim Speichern";
        this.notify(msg, "error");
      } finally {
        this.form.saving = false;
      }
    },

    confirmDelete(item) {
      this.deleteDialog.item = item;
      this.deleteDialog.deleting = false;
      this.deleteDialog.show = true;
    },

    async deleteProduct() {
      this.deleteDialog.deleting = true;
      try {
        await productionService.deleteEmbroideryProduct(this.deleteDialog.item.id);
        this.deleteDialog.show = false;
        this.notify("Produkt gelöscht");
        await this.fetchProducts();
      } catch (err) {
        this.notify("Fehler beim Löschen", "error");
      } finally {
        this.deleteDialog.deleting = false;
      }
    },

    notify(text, color = "success") {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
  },
};
</script>
